import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  doc,
  updateDoc,
  setDoc, // setDoc eklendi
  orderBy,
  limit 
} from 'firebase/firestore';
import { db } from './firebase';

class TeacherService {
  // Tüm öğretmenleri getir
  async getAllTeachers(filters = {}) {
    try {
      let q = collection(db, 'teachers');
      const constraints = [where('isActive', '==', true)];

      // Filtreler
      if (filters.subjects && filters.subjects.length > 0) {
        constraints.push(where('subjects', 'array-contains-any', filters.subjects));
      }

      if (filters.languages && filters.languages.length > 0) {
        constraints.push(where('languages', 'array-contains-any', filters.languages));
      }

      if (filters.minPrice) {
        constraints.push(where('hourlyRate', '>=', parseFloat(filters.minPrice)));
      }

      if (filters.maxPrice) {
        constraints.push(where('hourlyRate', '<=', parseFloat(filters.maxPrice)));
      }

      if (filters.sortBy === 'rating') {
        constraints.push(orderBy('rating', 'desc'));
      } else if (filters.sortBy === 'price-low') {
        constraints.push(orderBy('hourlyRate', 'asc'));
      } else if (filters.sortBy === 'price-high') {
        constraints.push(orderBy('hourlyRate', 'desc'));
      }

      q = query(q, ...constraints);

      const snapshot = await getDocs(q);
      const teachers = [];
      
      snapshot.forEach((doc) => {
        teachers.push({ id: doc.id, ...doc.data() });
      });

      return teachers;
    } catch (error) {
      console.error('Get teachers error:', error);
      throw error;
    }
  }

  // Tek bir öğretmen getir
  async getTeacherById(teacherId) {
    try {
      const docRef = doc(db, 'teachers', teacherId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Teacher not found');
      }
    } catch (error) {
      console.error('Get teacher error:', error);
      throw error;
    }
  }

  // Öğretmen profilini güncelle
  async updateTeacherProfile(teacherId, data) {
    try {
      const docRef = doc(db, 'teachers', teacherId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Update teacher error:', error);
      throw error;
    }
  }

  // Öğretmen yorumlarını getir
  async getTeacherReviews(teacherId) {
    try {
      const q = query(
        collection(db, 'reviews'),
        where('teacherId', '==', teacherId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const reviews = [];
      
      snapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });

      return reviews;
    } catch (error) {
      console.error('Get reviews error:', error);
      throw error;
    }
  }

  // Popüler öğretmenleri getir
  async getPopularTeachers(limitCount = 8) {
    try {
      const q = query(
        collection(db, 'teachers'),
        where('isActive', '==', true),
        orderBy('rating', 'desc'),
        orderBy('lessonsCompleted', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const teachers = [];
      
      snapshot.forEach((doc) => {
        teachers.push({ id: doc.id, ...doc.data() });
      });

      return teachers;
    } catch (error) {
      console.error('Get popular teachers error:', error);
      throw error;
    }
  }

  // Öğretmen arama
  async searchTeachers(searchTerm) {
    try {
      // Basit arama - gerçek uygulamada Algolia veya ElasticSearch kullanılabilir
      const allTeachers = await this.getAllTeachers();
      
      const filtered = allTeachers.filter(teacher => {
        const fullName = `${teacher.firstName} ${teacher.lastName}`.toLowerCase();
        const bio = teacher.bio?.toLowerCase() || '';
        const subjects = teacher.subjects.join(' ').toLowerCase();
        
        const term = searchTerm.toLowerCase();
        
        return fullName.includes(term) || 
               bio.includes(term) || 
               subjects.includes(term);
      });

      return filtered;
    } catch (error) {
      console.error('Search teachers error:', error);
      throw error;
    }
  }

  /**
   * Mevcut bir kullanıcıyı öğretmen statüsüne yükseltir
   */
  async upgradeToTeacher(userId, teacherData) {
    try {
      // 1. Önce User dokümanındaki rolü 'teacher' yap
      // Bu işlem, firestore.rules'daki "isTeacher()" kontrolünün geçmesi için gereklidir.
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: 'teacher',
        updatedAt: new Date().toISOString()
      });

      // 2. Teacher profil dokümanını oluştur
      const teacherRef = doc(db, 'teachers', userId);
      await setDoc(teacherRef, {
        uid: userId,
        firstName: teacherData.firstName,
        lastName: teacherData.lastName,
        email: teacherData.email,
        subjects: teacherData.subjects || [],
        languages: ['English'], // Varsayılan dil, kullanıcı daha sonra güncelleyebilir
        hourlyRate: parseFloat(teacherData.hourlyRate) || 10,
        bio: teacherData.bio || '',
        education: [],
        experience: [],
        certifications: [],
        availability: {},
        rating: 0,
        reviewCount: 0,
        lessonsCompleted: 0,
        videoIntroUrl: null,
        photoURL: teacherData.photoURL || null,
        isActive: true,
        createdAt: new Date().toISOString()
      });

      return true;
    } catch (error) {
      console.error('Upgrade to teacher error:', error);
      throw error;
    }
  }
}

export default new TeacherService();